import { Component } from 'react';

import { COPY, type SourceNoun } from '../copy';
import { openPickerTab } from '../state/picker-url';
import { currentPickerTabHref, PICKER_TAB_ID } from './picker-tab-href';
import styles from './picker-nudge.component.module.scss';

export interface PickOnVideoLinkProps {
    /**
     * Tell the owner the picker was asked for. The owner records `FRAMEPICKER_OPENED
     * {source:'time_link'}` on arrival (§8); this link only asks. Optional so the link can be
     * dropped in with nothing but the URL write.
     */
    onOpen?: () => void;
    /** Override the href (specs; a page rendered without a window). Defaults to the live URL. */
    href?: string;
    /** R14 (slice H, R14U-13): the page's noun ("GIF" on `convert-gif-to-jpg`); defaults to the one read off the page's path. */
    noun?: SourceNoun;
}

/**
 * "Pick it on the video" — the text link on Quick convert (FRAME_PICKER_DESIGN.md §6.1).
 *
 * WHERE IT RENDERS is the owner's decision: §6.1 puts it in `file-convert.component.tsx` ABOVE
 * the Advanced Options accordion ("not inside the options panel, which has no React insertion
 * point besides `eval`'d notes"), gated exactly like the tab (page gate + `featureFlags.
 * framePicker !== false`, D1: flag off hides the link).
 *
 * THE WRITE goes through the picker's own URL module (`state/picker-url.ts#openPickerTab`,
 * which is `writeTabParam('picker')` underneath) — import, never re-define (Global
 * Constraints). It composes with any other write in the same tick, it carries the dynamic
 * segment, it is shallow, and it never touches `history.pushState` (route-state's header says
 * why). Only the picker's own key is written: the Studio's `tab`/`sid`/`step`/`job` are never
 * cleared by the picker (§0 "Tab state"), and `psid` is the picker root's to set once it has a
 * session (`replacePickerSession`) — this link has no session, so `openPickerTab()` is called bare.
 *
 * A REAL ANCHOR with the same URL as its href, so modified clicks (new tab), a router that has
 * not mounted, and assistive tech all get a link that works; the JS path only makes the plain
 * click shallow.
 */
/**
 * Review 7 (PAGE lane): the server rendered `href="?tab=picker"` (no window) and the client's FIRST render the full
 * `/convert-mov-to-jpg?tab=picker` (and the noun from the path) -- a hydration mismatch that can re-render the subtree. The
 * first render is now the server's exactly; the page-specific href and noun are filled in after mount.
 */
interface PickOnVideoLinkState {
    mounted: boolean;
}

export class PickOnVideoLinkComponent extends Component<PickOnVideoLinkProps, PickOnVideoLinkState> {
    state: PickOnVideoLinkState = { mounted: false };

    componentDidMount() {
        this.setState({ mounted: true });
    }

    private onClick = (e: { metaKey?: boolean; ctrlKey?: boolean; shiftKey?: boolean; button?: number; preventDefault?: () => void }) => {
        if (e?.metaKey || e?.ctrlKey || e?.shiftKey || e?.button === 1) return;
        e?.preventDefault?.();
        openPickerTab(undefined, 'time_link'); // IT1 (saas #2): the root counts FRAMEPICKER_OPENED{time_link}
        this.props.onOpen?.();
    };

    /** The page's noun: the prop, else the path (`/convert-gif-to-jpg`), else "video". */
    private noun(): SourceNoun {
        if (this.props.noun) return this.props.noun;
        return COPY.sourceNounOfSlug(this.state.mounted && typeof window !== 'undefined' ? window.location.pathname : '');
    }

    render() {
        const href = this.props.href ?? (this.state.mounted ? currentPickerTabHref() : null) ?? `?tab=${PICKER_TAB_ID}`;
        return (
            <a
                href={href}
                className={styles['pick-link']}
                data-testid="pick-on-video-link"
                onClick={this.onClick}
            >
                {/* R14 (slice H, R14U-13): named for what it does and for the page's file -- never "video" on a GIF page. */}
                {COPY.pickItOnFor(this.noun())}
            </a>
        );
    }
}

export default PickOnVideoLinkComponent;
